import { useNavigate } from '@tanstack/react-router';
import { ArrowRightLeft, DoorOpen, ExternalLink, FastForward, MessagesSquare, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { DEBRIEF_ONLY, INTERVIEW_STAGES, canRecordWithdrawal, type ApplicationStage, type PipelineCan, type PipelineOptions, type PipelineRow, type ScreeningGuard } from '@nest-recruit/shared';
import { MenuGroup, MenuItem, MenuSeparator, Popover } from '../../../components/Popover';
import type { AnchorRect } from '../../../lib/anchored-panel';
import { useToasts } from '../../../stores/toasts';
import { WithdrawalPicker } from '../../candidates/components/WithdrawalPicker';
import { heldInterviewFor } from '../../decisions/api/decisions-api';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import type { usePipelineActions } from '../hooks/use-pipeline-actions';
import { CONTACT_ATTEMPTS_CHECKED_BY_SERVER, intentFor, isMoveTarget, reasonOptions, type MoveTarget } from '../move-intent';
import { SkipPrompt } from './SkipPrompt';
import { TransferPicker } from './TransferPicker';
import { useModule, useStageLabels } from '../../../lib/modules';

type Popped =
  | { readonly kind: 'reject' | 'more' | 'transfer' | 'withdraw'; readonly anchor: AnchorRect; readonly row: PipelineRow }
  | { readonly kind: 'skip'; readonly anchor: AnchorRect; readonly row: PipelineRow; readonly to: MoveTarget; readonly skippedLabels: readonly string[] };

interface RowMovesOptions {
  readonly jobId: string;
  readonly activeStages: readonly ApplicationStage[];
  readonly can: PipelineCan | undefined;
  readonly options: PipelineOptions | undefined;
  readonly actions: ReturnType<typeof usePipelineActions>;
  readonly via: 'board' | 'list';
}

export interface RowMoves {
  /** Move one application: straight away, or after its reason (reject) or note (an HR Lead's skip). */
  request(row: PipelineRow, to: ApplicationStage, anchor: AnchorRect): void;
  /** The ⋯ menu — every move by keyboard or touch, where a drag is awkward (06-qol §2). */
  openMenu(row: PipelineRow, anchor: AnchorRect): void;
  readonly popover: ReactNode;
}

/**
 * One application's moves, the same from a list row and a board card (F8.13 "same
 * validation as button actions"): the shared rules choose what to ask, the pickers
 * anchor to what was clicked or dropped on (CLAUDE.md §5), and the server decides.
 */
export function useRowMoves({ jobId, activeStages, can, options, actions, via }: RowMovesOptions): RowMoves {
  const stageLabels = useStageLabels();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const [popped, setPopped] = useState<Popped | null>(null);
  const active = new Set(activeStages);
  const canSkip = can?.skip === true;
  // D91: the screening-order guard, on by default, with an admin switch to turn it off (Module switches).
  const isScreeningGuardOn = useModule('screening_order');
  const guardFor = (row: PipelineRow): ScreeningGuard => ({ isOn: isScreeningGuardOn, managerResponse: row.managerResponse });
  const close = (): void => setPopped(null);

  async function openDebrief(row: PipelineRow): Promise<void> {
    const interviewId = await heldInterviewFor(row.id, row.stage).catch(() => null);
    if (interviewId) return void navigate({ to: '/interviews/$interviewId/debrief', params: { interviewId } });
    show({ tone: 'warning', message: `${row.candidate.fullName}'s interview hasn't been held yet`, detail: 'The round is decided on its debrief once the interview is over and the scorecards are in.' });
  }

  function request(row: PipelineRow, to: ApplicationStage, anchor: AnchorRect): void {
    const intent = intentFor(row.stage, to, active, canSkip, guardFor(row));
    if (intent.kind === 'refused' || !isMoveTarget(to)) {
      const isDebrief = intent.kind === 'refused' && intent.reason === DEBRIEF_ONLY;
      show({
        tone: 'warning',
        message: `${row.candidate.fullName} stays in ${stageLabels[row.stage]}`,
        detail: intent.kind === 'refused' ? intent.reason : "It can't be moved there.",
        // S21 Q4: the move belongs to the round's debrief — one click to it.
        ...(isDebrief ? { action: { label: 'Open the debrief', run: () => void openDebrief(row) } } : {}),
      });
      return;
    }
    if (intent.kind === 'reject') return setPopped({ kind: 'reject', anchor, row });
    if (intent.kind === 'skip') return setPopped({ kind: 'skip', anchor, row, to, skippedLabels: intent.skipped.map((stage) => stageLabels[stage]) });
    if (intent.kind === 'skip_screening') return setPopped({ kind: 'skip', anchor, row, to, skippedLabels: ['Review', 'Shortlist', 'Contact'] });
    close();
    void actions.moveOne(row, { to }, via);
  }

  const popover = (
    <Popover anchor={popped?.anchor ?? null} onClose={close} align="right" label={popped ? `${popped.row.candidate.fullName}` : 'Move'}>
      {popped?.kind === 'reject' && (
        <ReasonPicker
          mode="reject"
          reasons={reasonOptions(options?.reasons ?? [], [popped.row.stage])}
          pools={options?.pools ?? []}
          canPool={can?.pool === true}
          contactAttempts={CONTACT_ATTEMPTS_CHECKED_BY_SERVER}
          candidateName={popped.row.candidate.fullName}
          canAddToCvBank
          onConfirm={(choice) => {
            close();
            void actions.moveOne(popped.row, { to: 'rejected', reasonCode: choice.reasonCode, poolIds: [...choice.poolIds], newPoolNames: [...choice.newPoolNames], addToCvBank: choice.addToCvBank }, via);
          }}
        />
      )}
      {popped?.kind === 'skip' && (
        <SkipPrompt
          candidateName={popped.row.candidate.fullName}
          toLabel={stageLabels[popped.to]}
          skipped={popped.skippedLabels}
          onConfirm={(skipReason) => {
            close();
            void actions.moveOne(popped.row, { to: popped.to, skipReason }, via);
          }}
        />
      )}
      {popped?.kind === 'withdraw' && <WithdrawalPicker applicationId={popped.row.id} version={popped.row.version} candidateName={popped.row.candidate.fullName} onDone={close} />}
      {popped?.kind === 'transfer' && <TransferPicker jobId={jobId} row={popped.row} canSkip={canSkip} onDone={close} />}
      {popped?.kind === 'more' && (
        <MoveMenu
          row={popped.row}
          active={active}
          canMove={can?.move === true}
          canSkip={canSkip}
          screeningGuard={guardFor(popped.row)}
          onMove={(to) => request(popped.row, to, popped.anchor)}
          onTransfer={() => setPopped({ kind: 'transfer', anchor: popped.anchor, row: popped.row })}
          onWithdraw={() => setPopped({ kind: 'withdraw', anchor: popped.anchor, row: popped.row })}
          onOpen={() => void navigate({ to: '/applications/$applicationId', params: { applicationId: popped.row.id } })}
          onDebrief={() => {
            const { row } = popped;
            close();
            void openDebrief(row);
          }}
        />
      )}
    </Popover>
  );

  return { request, openMenu: (row, anchor) => setPopped({ kind: 'more', anchor, row }), popover };
}

interface MoveMenuProps {
  readonly row: PipelineRow;
  readonly active: ReadonlySet<ApplicationStage>;
  readonly canMove: boolean;
  readonly canSkip: boolean;
  readonly screeningGuard: ScreeningGuard;
  readonly onMove: (to: ApplicationStage) => void;
  readonly onTransfer: () => void;
  readonly onWithdraw: () => void;
  readonly onOpen: () => void;
  readonly onDebrief: () => void;
}

const INTERVIEWS: readonly ApplicationStage[] = INTERVIEW_STAGES;

/** "Move to…" lists only where this person may send this application; the board says why not elsewhere. */
function MoveMenu({ row, active, canMove, canSkip, screeningGuard, onMove, onTransfer, onWithdraw, onOpen, onDebrief }: MoveMenuProps): ReactNode {
  const stageLabels = useStageLabels();
  const stages: readonly ApplicationStage[] = [...new Set<ApplicationStage>([...active, 'hold'])];
  const targets = stages.flatMap((stage) => {
    const intent = intentFor(row.stage, stage, active, canSkip, screeningGuard);
    return intent.kind === 'move' || intent.kind === 'skip' || intent.kind === 'skip_screening' ? [{ stage, isSkip: intent.kind !== 'move' }] : [];
  });
  const canReject = intentFor(row.stage, 'rejected', active, canSkip, screeningGuard).kind === 'reject';
  return (
    <>
      {/* S21 Q4: an interview round moves on from its debrief, so the menu goes straight there. */}
      {INTERVIEWS.includes(row.stage) && (
        <MenuItem icon={<MessagesSquare size={14} aria-hidden />} onClick={onDebrief}>
          Open the debrief
        </MenuItem>
      )}
      {canMove && targets.length > 0 && (
        <MenuGroup label="Move to">
          {targets.map(({ stage, isSkip }) => (
            <MenuItem key={stage} icon={<FastForward size={14} aria-hidden />} meta={isSkip ? 'Skip' : undefined} onClick={() => onMove(stage)}>
              {stageLabels[stage]}
            </MenuItem>
          ))}
        </MenuGroup>
      )}
      {canMove && canReject && (
        <MenuItem icon={<XCircle size={14} aria-hidden />} isDanger onClick={() => onMove('rejected')}>
          Reject…
        </MenuItem>
      )}
      {canMove && (
        <MenuItem icon={<ArrowRightLeft size={14} aria-hidden />} onClick={onTransfer}>
          Transfer to another job…
        </MenuItem>
      )}
      {canMove && canRecordWithdrawal(row.stage) && (
        <MenuItem icon={<DoorOpen size={14} aria-hidden />} onClick={onWithdraw}>
          Record withdrawal…
        </MenuItem>
      )}
      <MenuSeparator />
      <MenuItem icon={<ExternalLink size={14} aria-hidden />} onClick={onOpen}>
        Open application
      </MenuItem>
    </>
  );
}

